import type { ChangeEvent, FormEvent } from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Box, Typography, Paper, Button, TextField, Grid, IconButton, CircularProgress, Alert } from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { URLHandler } from "src/services/URLHandler";
import { useCreateApplication } from "src/api/client-api/applications/useCreateApplication";
import { Application } from "src/api/client-api/applications/useGetAllApplications";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import { SecretRevealDialog } from "./components/SecretRevealDialog";

export const ApplicationCreate = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const createApplicationMutation = useCreateApplication();
    const isCreating = createApplicationMutation.isLoading;

    const [formData, setFormData] = useState<Omit<Application, "id" | "createdAt" | "updatedAt">>({
        name: "",
        description: "",
        secretKey: "",
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [createdApplication, setCreatedApplication] = useState<{ id: string; secretKey: string } | null>(null);

    // Reset all states when component mounts
    useEffect(() => {
        // Reset form states
        setFormData({
            name: "",
            description: "",
            secretKey: "",
        });
        setFormErrors({});
        setError(null);
        setTouched({});

        // Reset mutation state to clear any previous loading/error states
        createApplicationMutation.reset();
    }, []); // Empty dependency array means this runs once on mount

    // Handle form input changes
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Mark field as touched
        if (!touched[name]) {
            setTouched((prev) => ({
                ...prev,
                [name]: true,
            }));
        }
    };

    // Validate form on touched fields change
    useEffect(() => {
        // Only validate touched fields
        const touchedErrors: Record<string, string> = {};

        Object.keys(touched).forEach((field) => {
            if (touched[field]) {
                if (field === "name" && !formData.name) {
                    touchedErrors.name = t("applications.nameRequired", "Name is required");
                }
                if (field === "description" && !formData.description) {
                    touchedErrors.description = t("applications.descriptionRequired", "Description is required");
                }
            }
        });

        setFormErrors(touchedErrors);
    }, [formData, touched, t]);

    // Validate all fields before submission
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.name) {
            errors.name = t("applications.nameRequired", "Name is required");
        }

        if (!formData.description) {
            errors.description = t("applications.descriptionRequired", "Description is required");
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);

        // Mark all fields as touched for validation
        const allTouched = Object.keys(formData).reduce(
            (acc, key) => {
                acc[key] = true;
                return acc;
            },
            {} as Record<string, boolean>,
        );

        setTouched(allTouched);

        if (!validateForm()) {
            return;
        }

        try {
            const response = await createApplicationMutation.mutateAsync({
                application: formData,
            });

            setSnackbarProps({
                message: t("applications.createSuccess", "Application created successfully"),
                type: "success",
            });

            if (response.secretKey) {
                setCreatedApplication({ id: response.id, secretKey: response.secretKey });
            } else {
                navigate(URLHandler.settings.applications.edit(response.id));
            }
        } catch (validationErrors: any) {
            // If the error is a validation error object (from our API)
            if (validationErrors && typeof validationErrors === "object" && !validationErrors.message) {
                // These are field-specific validation errors
                setFormErrors(validationErrors);
            } else {
                // This is a general error
                setError(t("applications.createError", "Failed to create application"));
            }
        }
    };

    // Navigate back to applications list
    const handleBack = () => {
        navigate(URLHandler.settings.applications.list());
    };

    const handleSecretRevealAcknowledge = () => {
        if (createdApplication) {
            navigate(URLHandler.settings.applications.edit(createdApplication.id));
        }
    };

    return (
        <Box sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                        onClick={handleBack}
                        sx={{ mr: 2 }}
                        aria-label={t("applications.back", "Back to applications list")}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4">{t("applications.createNew", "Create New Application")}</Typography>
                </Box>
            </Box>

            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Application Form */}
            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                name="name"
                                label={t("applications.name", "Application Name")}
                                value={formData.name}
                                onChange={handleInputChange}
                                onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                                fullWidth
                                required
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                disabled={isCreating}
                                inputProps={{
                                    "aria-required": "true",
                                    "aria-invalid": !!formErrors.name,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="description"
                                label={t("applications.description", "Description")}
                                value={formData.description}
                                onChange={handleInputChange}
                                onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
                                fullWidth
                                required
                                multiline
                                rows={3}
                                error={!!formErrors.description}
                                helperText={formErrors.description}
                                disabled={isCreating}
                                inputProps={{
                                    "aria-required": "true",
                                    "aria-invalid": !!formErrors.description,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                                {/* Cancel/Reset button - Secondary style */}
                                <Button
                                    variant="outlined"
                                    onClick={handleBack}
                                    disabled={isCreating}
                                    sx={{
                                        borderRadius: "44px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "0.875rem",
                                        lineHeight: "20px",
                                        padding: "10px 20px",
                                        height: "40px",
                                        borderColor: "#E7E6E4",
                                        color: "#393A36",
                                        "&:hover": {
                                            borderColor: "#D1D0CE",
                                            backgroundColor: "rgba(57, 58, 54, 0.04)",
                                        },
                                        "&:disabled": {
                                            opacity: 0.36,
                                            borderColor: "#E7E6E4",
                                            color: "#393A36",
                                        },
                                    }}
                                >
                                    {t("applications.cancel", "Annuler")}
                                </Button>

                                {/* Create button - Primary style */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isCreating}
                                    sx={(theme) => ({
                                        borderRadius: "44px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "0.875rem",
                                        lineHeight: "20px",
                                        padding: "10px 20px",
                                        height: "40px",
                                        width: "140px",
                                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                        color: "white !important",
                                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                        "&:hover": {
                                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                        },
                                        "&:disabled": {
                                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                            color: "white !important",
                                            opacity: 0.36,
                                        },
                                    })}
                                >
                                    {isCreating ?
                                        <CircularProgress size={20} color="inherit" />
                                    :   t("applications.create", "Créer")}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <SecretRevealDialog
                open={createdApplication !== null}
                secretKey={createdApplication?.secretKey ?? null}
                title={t("applications.secretCreatedTitle", "Application secret created")}
                description={t(
                    "applications.secretCreatedDescription",
                    "Your application was created. Save this secret key now — it won't be shown again.",
                )}
                onAcknowledge={handleSecretRevealAcknowledge}
            />
        </Box>
    );
};

export default ApplicationCreate;
